import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { ethers } from "ethers";
import { useListningQuery, useMintOwnerAssetMutation } from "../../../redux/api/authApi";
import ManageListingsSkeleton from "./ManageListingsSkeleton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate } from "react-router-dom";
import AssetOwnerImage from "../AssetOwnerImage";

function formatCurrencyValue(num) {
  if (num == null || Number.isNaN(Number(num))) return "—";
  return `SAR ${Number(num).toLocaleString("en-US")}`;
}

function getListingLocation(listing) {
  const addr = listing.basicInfo?.completeAddress;
  if (addr) {
    const city = addr.city?.name;
    const country = addr.country?.name;
    const parts = [city, country].filter(Boolean);
    if (parts.length) return parts.join(", ");
  }
  const commodityLoc = listing.basicInfo?.commodityAssetDetails?.location;
  if (commodityLoc) return commodityLoc;
  return "";
}

function getVerificationEntries(verificationStatus) {
  if (!verificationStatus || typeof verificationStatus !== "object") return [];
  return Object.entries(verificationStatus).filter(
    ([key, v]) =>
      v &&
      typeof v === "object" &&
      typeof v.verified === "boolean" &&
      key !== "message"
  );
}

function matchesListingTab(listing, tabKey) {
  const s = String(listing.status || "").toLowerCase();
  if (tabKey === "all") return true;
  if (tabKey === "active") return s === "active";
  if (tabKey === "pending") return s === "pending" || s === "under review";
  if (tabKey === "funded") return s ==='funded';
  // {
  //   const sold = listing.tokenEconomics?.tokensSold ?? 0;
  //   const supply = listing.tokenEconomics?.totalSupply ?? 0;
  //   return supply > 0 && sold === supply;
  // }
  return true;
}

const TAB_KEYS = ["all", "active", "funded", "pending"];

/** Fixed category filter values; must match `listing.assetCategory.name` from the API (case-insensitive). */
const CATEGORY_FILTER_OPTIONS = [
  { value: "all", label: "All Categories" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Commodities", label: "Commodities" },
  { value: "Luxury Goods", label: "Luxury Goods" },
];

function listingMatchesCategoryFilter(listing, categoryFilter) {
  if (categoryFilter === "all") return true;
  const apiName = String(listing.assetCategory?.name ?? "").trim().toLowerCase();
  const wanted = String(categoryFilter).trim().toLowerCase();
  return apiName === wanted;
}

function isUserRejectedWalletError(err) {
  const code = err?.code;
  if (code === "ACTION_REJECTED" || code === 4001) return true;
  const m = String(err?.message || err?.shortMessage || "").toLowerCase();
  return (
    m.includes("user rejected") ||
    m.includes("user denied") ||
    m.includes("rejected the request")
  );
}

/** Edit flow is allowed only for these listing statuses (case-insensitive). */
function isListingEditableByStatus(listing) {
  const s = String(listing?.status || "").trim().toLowerCase();
  return s === "rejected" || s === "pending";
}

/** Human label for `filesNeedToBeUpdated` API keys (camelCase / snake_case). */
function humanizeFileUpdateKey(key) {
  const k = String(key || "").trim();
  if (!k) return "";
  const lower = k.toLowerCase().replace(/-/g, "_");
  const map = {
    titledeed: "Title deed",
    title_deed: "Title deed",
    legaldocument: "Legal document",
    legaldocuments: "Legal documents",
    legal_document: "Legal document",
    valuationreport: "Valuation report",
    regacertificate: "REGA certificate",
    rega_certificate: "REGA certificate",
    mainimage: "Main image",
    galleryimages: "Gallery images",
    spvsignature: "SPV signature (authorized signatory)",
    spvdocuments: "SPV documents",
    spv_document: "SPV document",
  };
  if (map[lower]) return map[lower];
  return k
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * When a rejection blob (`assetRejected`, `regaRejected`, `taqeemRejected`) has a reason
 * and/or `filesNeedToBeUpdated`, return { reason, files }; else null.
 */
function getRejectionPayloadFromObject(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
  const meaningfulKeys = Object.keys(obj).filter((k) => !["__v"].includes(k));
  if (meaningfulKeys.length === 0) return null;

  const reason = String(obj.reason ?? "").trim();
  const files = Array.isArray(obj.filesNeedToBeUpdated)
    ? obj.filesNeedToBeUpdated.map((x) => String(x ?? "").trim()).filter(Boolean)
    : [];

  if (!reason && files.length === 0) return null;

  return { reason, files };
}

/**
 * Rejection panels from API-only blobs: `assetRejected`, `regaRejected`, `taqeemRejected`.
 * Does not read top-level `rejectionReason` / `filesNeedToBeUpdated` on the listing.
 */
function getListingRejectionSections(listing) {
  const sections = [];

  const asset = getRejectionPayloadFromObject(listing?.assetRejected);
  if (asset && (asset.reason || asset.files.length > 0)) {
    sections.push({
      key: "asset",
      title: "Asset update required",
      reason: asset.reason,
      files: asset.files,
    });
  }

  const rega = getRejectionPayloadFromObject(listing?.regaRejected);
  if (rega && (rega.reason || rega.files.length > 0)) {
    sections.push({
      key: "rega",
      title: "REGA verification feedback",
      reason: rega.reason,
      files: rega.files,
    });
  }

  const taqeem = getRejectionPayloadFromObject(listing?.taqeemRejected);
  if (taqeem && (taqeem.reason || taqeem.files.length > 0)) {
    sections.push({
      key: "taqeem",
      title: "Valuation report (Taqeem) feedback",
      reason: taqeem.reason,
      files: taqeem.files,
    });
  }

  return sections;
}

const ManageListings = () => {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useListningQuery();
  const [mintOwnerAsset] = useMintOwnerAssetMutation();
  const [tabKey, setTabKey] = React.useState("all");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuListing, setMenuListing] = React.useState(null);
  const [mintingAssetId, setMintingAssetId] = React.useState(null);

  const listings = React.useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data?.data]
  );
  const filterCounts = data?.filterCounts || {};

  const filteredListings = React.useMemo(() => {
    return listings.filter((l) => {
      if (!matchesListingTab(l, tabKey)) return false;
      return listingMatchesCategoryFilter(l, categoryFilter);
    });
  }, [listings, tabKey, categoryFilter]);

  const handleListNewAsset = () => {
    navigate("/asset/list-new-asset");
  };

  const handleManageAsset = (listing) => {
    navigate(`/asset/property-details?id=${listing._id}`, {
      state: { listing },
    });
  };

  const handleEditAsset = (listing) => {
    const id = listing?._id;
    if (!id) return;
    navigate(`/asset/list-new-asset?edit=${encodeURIComponent(String(id))}`);
  };

  const handleMintAsset = async (listing) => {
    const assetId = listing?._id;
    if (!assetId) return;

    if (typeof window === "undefined" || !window.ethereum) {
      window.alert("Please install MetaMask to mint.");
      return;
    }

    setMintingAssetId(assetId);
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const message = `I authorize minting tokens for asset ${assetId}`;
      const signature = await signer.signMessage(message);

      await mintOwnerAsset({ assetId, signature, message }).unwrap();
      await refetch();
    } catch (err) {
      if (!isUserRejectedWalletError(err)) {
        console.error(err);
      }
    } finally {
      setMintingAssetId(null);
    }
  };

  const openRowMenu = (e, listing) => {
    setMenuAnchor(e.currentTarget);
    setMenuListing(listing);
  };

  const closeRowMenu = () => {
    setMenuAnchor(null);
    setMenuListing(null);
  };

  if (isLoading) {
    return <ManageListingsSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error" sx={{ mb: 2 }}>
          Failed to load listings.
        </Typography>
        <Button variant="outlined" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        display: "flex",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Button
          onClick={() => navigate("/asset/dashboard")}
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: "none",
            color: "#111827",
            mb: 2,
            px: 0,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Back to Dashboard
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 1,
                bgcolor: "background.headerIcon",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Inventory2OutlinedIcon sx={{ color: "text.slate" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                My Listings
              </Typography>
              <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
                Track performance and manage your tokenized assets.
              </Typography>
            </Box>
          </Stack>

          <Button
             variant="contained"
             startIcon={<AddIcon />}
             sx={{
               borderRadius: "12px",
               textTransform: "none",
               px: 3.5,
               py: 1.5,
               fontSize: 14,
               fontWeight: 600,
               boxShadow: (theme) => theme.palette.brand.ctaShadow,
               background: (theme) => theme.palette.brand.ctaGradient,
               "&:hover": {
                 boxShadow: (theme) => theme.palette.brand.ctaShadowHover,
                 background: (theme) => theme.palette.brand.ctaGradientHover,
               },
             }}
             onClick={handleListNewAsset}
          >
            List New Asset
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
            pb: 0,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              p: 0.5,
              borderRadius: 999,
              bgcolor: "background.default",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ overflowX: "auto", flexWrap: "wrap", rowGap: 0.5 }}
            >
              {TAB_KEYS.map((key) => {
                const labels = {
                  all: `All Assets (${filterCounts.all ?? listings.length})`,
                  active: `Active (${filterCounts.active ?? 0})`,
                  funded: `Funded (${filterCounts.funded ?? 0})`,
                  pending: `Pending (${filterCounts.pending ?? 0})`,
                };
                const selected = tabKey === key;
                return (
                  <Button
                    key={key}
                    onClick={() => setTabKey(key)}
                    disableRipple
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 600,
                      color: selected ? "common.white" : "text.muted",
                      bgcolor: selected
                        ? (theme) => theme.palette.accent.navy.button
                        : "transparent",
                      borderRadius: 999,
                      px: 2.25,
                      py: 0.75,
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                      boxShadow: selected
                        ? (theme) => theme.palette.shadow.statCard
                        : "none",
                      "&:hover": {
                        bgcolor: selected
                          ? (theme) => theme.palette.accent.navy.button
                          : (theme) =>
                              alpha(theme.palette.common.black, 0.04),
                        boxShadow: selected
                          ? (theme) => theme.palette.shadow.statCard
                          : "none",
                      },
                    }}
                  >
                    {labels[key]}
                  </Button>
                );
              })}
            </Stack>
          </Box>

          <FormControl
            size="small"
            sx={{
              minWidth: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 999,
                bgcolor: "background.paper",
                pl: 1.5,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.border.light,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.border.medium,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.palette.border.medium,
                  borderWidth: "1px",
                },
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: 14,
                fontWeight: 600,
                color: "text.primary",
                py: 1,
              },
            }}
          >
            <Select
              labelId="listing-category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              displayEmpty
              renderValue={(value) => {
                const label =
                  CATEGORY_FILTER_OPTIONS.find((c) => c.value === value)?.label ||
                  "All Categories";
                return (
                  <>
                    <FilterListIcon
                      sx={{ fontSize: 18, color: "text.muted" }}
                    />
                    <span>{label}</span>
                  </>
                );
              }}
            >
              {CATEGORY_FILTER_OPTIONS.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Stack spacing={2.5}>
          {filteredListings.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography color="text.secondary">
                No listings match this filter.
              </Typography>
            </Paper>
          ) : (
            filteredListings.map((listing) => {
              const vs = listing.verificationStatus || {};
              const verificationRows = getVerificationEntries(vs);
              const showVerificationPanel = !vs.allVerified && verificationRows.length > 0;
              const marketMessage = vs.message;
              const mainImageUrl = listing.assetImages?.mainImage?.fileUrl;
              const name = listing.basicInfo?.assetName || "Unnamed asset";
              const categoryName = listing.assetCategory?.name || "—";
              const listingStatus = listing.status || "Pending";
              const isApproved =
                String(listing.status || "").trim().toLowerCase() === "approved";
              const showEditButton = isListingEditableByStatus(listing);
              const allVerified = Boolean(vs.allVerified);
              const fin = listing.financialDetails || {};
              const viewsCount = Array.isArray(listing.viewedBy)
                ? listing.viewedBy.length
                : 0;
              const investorsDisplay = listing.investorCount || 0;
              const rejectionSections = getListingRejectionSections(listing);
              return (
                <Paper
                  key={listing._id}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0px 10px 40px rgba(15,23,42,0.06)",
                    bgcolor: "#ffffff",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      alignItems: "stretch",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "100%", md: 220 },
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "#f3f4f6",
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          minHeight: { xs: 200, md: 220 },
                          flex: 1,
                        }}
                      >
                        <AssetOwnerImage
                          src={mainImageUrl}
                          alt={name}
                          style={{
                            width: "100%",
                            height: "100%",
                            minHeight: 200,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </Box>
                      {marketMessage ? (
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1.25,
                            bgcolor: "#fff7ed",
                            borderTop: "1px solid #fed7aa",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: 18, color: "#c2410c", mt: 0.1 }}
                          />
                          <Typography variant="caption" sx={{ color: "#9a3412", lineHeight: 1.4 }}>
                            {marketMessage}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>

                    <Box sx={{ flex: 1, p: 2.5, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 700, color: "#111827" }}
                            >
                              {name}
                            </Typography>
                            <Chip
                              label={allVerified ? "Verified" : "Unverified"}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                bgcolor: allVerified ? "#dcfce7" : "#ffedd5",
                                color: allVerified ? "#15803d" : "#c2410c",
                              }}
                            />
                            <Chip
                              label={categoryName}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 500,
                                bgcolor: "#f3f4f6",
                                color: "#4b5563",
                              }}
                            />
                            <Chip
                              label={listingStatus}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                bgcolor: "#e5e7eb",
                                color: "#374151",
                              }}
                            />
                          </Stack>
                          {getListingLocation(listing) ? (
                            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.75 }}>
                              {getListingLocation(listing)}
                            </Typography>
                          ) : null}
                        </Box>
                        <IconButton size="small" onClick={(e) => openRowMenu(e, listing)}>
                          <MoreVertIcon sx={{ color: "#9ca3af" }} />
                        </IconButton>
                      </Box>

                      {rejectionSections.length > 0 ? (
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                          {rejectionSections.map((section) => (
                            <Box
                              key={section.key}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "#fef2f2",
                                border: "1px solid #fecaca",
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="flex-start"
                                spacing={1}
                                sx={{ mb: section.files.length ? 1 : 0 }}
                              >
                                <ErrorOutlineIcon
                                  sx={{ fontSize: 20, color: "#b91c1c", mt: 0.15, flexShrink: 0 }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700, color: "#991b1b", fontSize: 14 }}>
                                    {section.title}
                                  </Typography>
                                  {section.reason ? (
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#7f1d1d", mt: 0.75, lineHeight: 1.5 }}
                                    >
                                      {section.reason}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Stack>
                              {section.files.length > 0 ? (
                                <Box sx={{ pl: 0.5, mt: section.reason ? 1.25 : 0 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, color: "#991b1b", display: "block", mb: 0.75 }}
                                  >
                                    Files to re-upload
                                  </Typography>
                                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.25, color: "#7f1d1d" }}>
                                    {section.files.map((f) => (
                                      <Typography key={f} component="li" variant="body2" sx={{ lineHeight: 1.45 }}>
                                        {humanizeFileUpdateKey(f)}
                                      </Typography>
                                    ))}
                                  </Stack>
                                </Box>
                              ) : null}
                            </Box>
                          ))}
                        </Stack>
                      ) : null}

                      {showVerificationPanel ? (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "#fefce8",
                            border: "1px solid #fde047",
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: "#854d0e", fontSize: 15 }}>
                            Asset verification required
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#a16207", display: "block", mb: 1.5 }}>
                            Complete all verifications to submit this asset for admin review.
                          </Typography>
                          <Stack spacing={1.25}>
                            {verificationRows.map(([label, row]) => {
                              const ok = row.verified;
                              const statusLabel =
                                String(row.status || "").includes("Approved") && ok
                                  ? label.includes("Taqeem")
                                    ? "Valued"
                                    : "Verified"
                                  : row.status || "Pending";
                              return (
                                <Box
                                  key={label}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    py: 0.75,
                                    borderBottom: "1px solid rgba(234,179,8,0.35)",
                                    "&:last-of-type": { borderBottom: "none" },
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        color: "#713f12",
                                        letterSpacing: 0.4,
                                        fontSize: 11,
                                        display: "block",
                                      }}
                                    >
                                      {label.toUpperCase()}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#a16207" }}>
                                      {row.text || "—"}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    icon={
                                      ok ? (
                                        <CheckCircleIcon sx={{ fontSize: "16px !important" }} />
                                      ) : undefined
                                    }
                                    label={statusLabel}
                                    size="small"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: 11,
                                      bgcolor: ok ? "#dcfce7" : "#ffedd5",
                                      color: ok ? "#166534" : "#9a3412",
                                      "& .MuiChip-icon": { color: "inherit" },
                                    }}
                                  />
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      ) : null}

                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Investors
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {investorsDisplay}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Expected ROI
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "#16a34a" }}>
                            {fin.expectedROI != null ? `${fin.expectedROI}%` : "—"}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Total value
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrencyValue(fin.totalAssetValue)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Views
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {viewsCount.toLocaleString("en-US")}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        sx={{ mt: 2, width: "100%" }}
                      >
                        {isApproved ? (
                          <Button
                            fullWidth
                            variant="contained"
                            disabled={mintingAssetId === listing._id}
                            onClick={() => handleMintAsset(listing)}
                            startIcon={
                              mintingAssetId === listing._id ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : undefined
                            }
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              py: 1.1,
                              fontWeight: 700,
                              bgcolor: "#0d9488",
                              "&:hover": { bgcolor: "#0f766e" },
                            }}
                          >
                            {mintingAssetId === listing._id ? "Minting…" : "Mint"}
                          </Button>
                        ) : null}
                        {showEditButton ? (
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => handleEditAsset(listing)}
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              borderColor: "#0d9488",
                              color: "#0d9488",
                              py: 1.1,
                              fontWeight: 600,
                              "&:hover": {
                                borderColor: "#0f766e",
                                bgcolor: "rgba(13,148,136,0.06)",
                              },
                            }}
                          >
                            Edit
                          </Button>
                        ) : null}
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<RemoveRedEyeOutlinedIcon />}
                          onClick={() => handleManageAsset(listing)}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            borderColor: "#e5e7eb",
                            color: "#374151",
                            py: 1.1,
                            fontWeight: 600,
                            "&:hover": {
                              borderColor: "#d1d5db",
                              bgcolor: "#f9fafb",
                            },
                          }}
                        >
                          Manage asset
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Paper>
              );
            })
          )}
        </Stack>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuListing &&
        String(menuListing.status || "").trim().toLowerCase() === "approved" ? (
          <MenuItem
            disabled={mintingAssetId === menuListing._id}
            onClick={() => {
              closeRowMenu();
              handleMintAsset(menuListing);
            }}
          >
            <ListItemText
              primary={mintingAssetId === menuListing._id ? "Minting…" : "Mint"}
            />
          </MenuItem>
        ) : null}
        {menuListing && isListingEditableByStatus(menuListing) ? (
          <MenuItem
            onClick={() => {
              if (menuListing) handleEditAsset(menuListing);
              closeRowMenu();
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit asset" />
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            if (menuListing) handleManageAsset(menuListing);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <RemoveRedEyeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Manage asset" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ManageListings;
